import { Params, RouterStateSnapshot } from '@angular/router';
import { ActionReducerMap, createFeatureSelector, MetaReducer } from '@ngrx/store';
import { IDeviceState } from '@app/shared/model/interfaces/store/device-state';
import { ISearchState } from '@app/shared/model/interfaces/store/search-state';
import { IRouteState } from '@app/shared/model/interfaces/store/route-state';
import { environment } from '@env/environment';

import * as fromRouter from '@ngrx/router-store';
import * as fromDevice from '@app/store/reducers/device.reducer';
import * as fromSearch from '@app/store/reducers/search.reducer';
import * as fromMmao from '@app/store/reducers/mmao.reducer';
import * as fromHeader from '@app/core/header/store/reducers/header.reducer';
import * as fromI18n from '@app/core/i18n/store/reducers/i18n.reducers';
import * as fromRoute from '@app/store/reducers/route.reducer';

/**
 * The RouterStateSerializer takes the current RouterStateSnapshot
 * and returns any pertinent information needed. The snapshot contains
 * all information about the state of the router at the given point in time.
 * The entire snapshot is complex and not always needed. In this case, you only
 * need the URL and query parameters from the snapshot in the store. Other items could be
 * returned such as route parameters and static route data.
 */
export interface RouterStateUrl {
  url: string;
  queryParams: Params;
  params: Params;
}

@Injectable()
export class CustomRouterStateSerializer implements fromRouter.RouterStateSerializer<RouterStateUrl> {
  serialize(routerState: RouterStateSnapshot): RouterStateUrl {
    let route = routerState.root;

    while (route.firstChild) {
      route = route.firstChild;
    }

    const {
      url,
      root: { queryParams }
    } = routerState;
    const { params } = route;

    // Only return an object including the URL, params
    // and query params instead of the entire snapshot
    return { url, params, queryParams };
  }
}

/**
 * storeFreeze prevents state from being mutated. When mutation occurs, an
 * exception will be thrown. This is useful during development mode to
 * ensure that none of the reducers accidentally mutates the state.
 */
import { storeFreeze } from 'ngrx-store-freeze';
import { IMmao } from '@app/store/reducers/mmao.reducer';
import { IHeaderState } from '@app/core/header/models/header-state.model';
import { Injectable } from '@angular/core';

export interface IAppState {
  device: IDeviceState;
  search: ISearchState;
  router: fromRouter.RouterReducerState<RouterStateUrl>;
  mmao: IMmao;
  header: IHeaderState;
  i18n: fromI18n.I18nState;
  route: IRouteState;
}

/**
 * Our state is composed of a map of action reducer functions.
 * These reducer functions are called with each dispatched action
 * and the current or initial state and return a new immutable state.
 */
export const reducers: ActionReducerMap<IAppState> = {
  device: fromDevice.reducer,
  search: fromSearch.reducer,
  router: fromRouter.routerReducer,
  mmao: fromMmao.reducer,
  header: fromHeader.reducer,
  i18n: fromI18n.reducer,
  route: fromRoute.reducer
};

/**
 * By default, @ngrx/store uses combineReducers with the reducer map to compose
 * the root meta-reducer. To add more meta-reducers, provide an array of meta-reducers
 * that will be composed to form the root meta-reducer.
 */
export const metaReducers: MetaReducer<IAppState>[] = !environment.production ? [storeFreeze] : [];

export const getRouterState = createFeatureSelector<fromRouter.RouterReducerState<RouterStateUrl>>('routerReducer');
